import { ringSegmentsHTML } from '@theme/r69-ring-sizes';

/*
 * ROTAS69 ring-size picker wiring, extracted from snippets/r69-ring-size-select.liquid so it can run
 * where the PDP snippet is NOT on the page — namely inside the quick-add modal, which injects the
 * product's markup via innerHTML on a collection/search page (injected inline scripts never execute,
 * and the PDP-only snippet script isn't present there). It exposes r69WireRingSize(root) and also
 * re-scans on the `r69:rewire` event that quick-add fires after it injects content. wire() is
 * idempotent (rss.__wired), and every lookup is scoped to the rss's OWN picker so a page can hold a
 * PDP picker and a modal picker at once without them driving each other.
 */
function wire(rss) {
  if (rss.__wired) return;

  // Scope to this rss's own variant picker / product container — NOT document — so the modal copy
  // and any PDP copy stay independent.
  const scope = rss.closest('variant-picker, .variant-picker, .product-information, product-component, .quick-add-modal') || document;
  const picker = rss.closest('variant-picker') || scope.querySelector('variant-picker[data-template-product-match="true"]') || scope.querySelector('variant-picker');
  const host = scope.querySelector('.variant-option--ringsize');
  const trigger = rss.querySelector('.rss__trigger');
  const panel = rss.querySelector('.rss__panel');
  if (!host || !trigger || !panel) return;

  // The native size control is EITHER a <select> (variant style "dropdowns") OR a radio <fieldset>
  // ("buttons"). Drive whichever exists; the theme's <variant-picker> reacts to a bubbling `change`
  // from either.
  const nat = (function () {
    const select = host.querySelector('select');
    if (select) {
      return {
        get: () => select.value,
        set: (v) => {
          if (select.value === v) return false;
          select.value = v;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        },
        available: (v) => {
          const o = Array.prototype.filter.call(select.options, (x) => x.value === v)[0];
          return o ? !o.disabled : false;
        },
        onChange: (cb) => select.addEventListener('change', cb),
      };
    }
    const radios = Array.prototype.slice.call(host.querySelectorAll('input[type="radio"]'));
    if (!radios.length) return null;
    const find = (v) => radios.filter((r) => r.value === v)[0];
    return {
      get: () => {
        const r = radios.filter((x) => x.checked)[0];
        return r ? r.value : (radios[0] && radios[0].value) || '';
      },
      set: (v) => {
        const r = find(v);
        if (!r || r.checked) return false;
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      },
      available: (v) => {
        const r = find(v);
        if (!r) return false;
        if (r.getAttribute('aria-disabled') === 'true') return false;
        if (r.dataset.optionAvailable === 'false') return false;
        return true;
      },
      onChange: (cb) => host.addEventListener('change', cb),
    };
  })();
  if (!nat) return;
  rss.__wired = true;

  const insideLabel = rss.getAttribute('data-inside-label') || 'Ø';
  let open = false;

  const optionAvailable = (value) => nat.available(value);

  function pick(value) {
    const srcOpt = rss.querySelector('.rss__opt[data-value="' + (window.CSS && CSS.escape ? CSS.escape(value) : value) + '"]');
    if (srcOpt && srcOpt.classList.contains('is-soldout')) return;
    nat.set(value);
    sync();
    close(true);
  }

  function sync() {
    const cv = nat.get();
    trigger.querySelector('.rss__current').innerHTML = ringSegmentsHTML(cv, insideLabel);
    const soldCurrent = !optionAvailable(cv);
    trigger.classList.toggle('is-soldout', soldCurrent);
    Array.prototype.forEach.call(panel.children, (opt) => {
      const v = opt.getAttribute('data-value');
      opt.classList.toggle('is-selected', v === cv);
      opt.setAttribute('aria-selected', v === cv ? 'true' : 'false');
    });
  }

  const selectable = () => Array.prototype.filter.call(panel.children, (o) => !o.classList.contains('is-soldout'));
  function focusOpt(i) { const o = selectable(); if (!o.length) return; if (i < 0) i = o.length - 1; if (i >= o.length) i = 0; o[i].focus(); }
  function openPanel() {
    if (open) return; open = true;
    // Fit the panel to the viewport so it never runs off-screen — the failure the owner hit inside
    // the short quick-add modal on a phone. Measure the room below and above the trigger; cap the
    // panel's height to whichever side is used (it scrolls internally), and flip it ABOVE the
    // trigger when there's more room up there.
    const tr = trigger.getBoundingClientRect();
    const below = window.innerHeight - tr.bottom - 20;
    const above = tr.top - 20;
    const flip = below < 220 && above > below;
    panel.style.maxHeight = Math.max(132, Math.min(360, flip ? above : below)) + 'px';
    rss.classList.toggle('rss-flip', flip);
    panel.hidden = false; trigger.setAttribute('aria-expanded', 'true'); rss.classList.add('rss-open');
    document.addEventListener('click', onOutside, true); document.addEventListener('keydown', onKey, true);
    const s = panel.querySelector('.is-selected:not(.is-soldout)') || selectable()[0]; if (s) s.focus();
  }
  function close(refocus) {
    if (!open) return; open = false; panel.hidden = true; trigger.setAttribute('aria-expanded', 'false'); rss.classList.remove('rss-open');
    document.removeEventListener('click', onOutside, true); document.removeEventListener('keydown', onKey, true);
    if (refocus) trigger.focus();
  }
  function onOutside(e) { if (!rss.contains(e.target)) close(false); }
  function onKey(e) {
    if (e.key === 'Escape') { close(true); return; }
    const o = selectable(); const cur = o.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusOpt(cur < 0 ? 0 : cur + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focusOpt(cur < 0 ? o.length - 1 : cur - 1); }
    else if ((e.key === 'Enter' || e.key === ' ') && document.activeElement && document.activeElement.classList.contains('rss__opt')) { e.preventDefault(); document.activeElement.click(); }
  }

  Array.prototype.forEach.call(panel.children, (opt) => {
    opt.addEventListener('click', (e) => { e.preventDefault(); pick(opt.getAttribute('data-value')); });
  });
  trigger.addEventListener('click', (e) => { e.preventDefault(); if (open) close(true); else openPanel(); });
  trigger.addEventListener('keydown', (e) => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openPanel(); } });

  nat.onChange(sync);
  if (picker) {
    const obs = new MutationObserver(() => { const v = nat.get(); if (v !== (trigger.__last || '')) { trigger.__last = v; sync(); } });
    obs.observe(picker, { subtree: true, childList: true, attributes: true, attributeFilter: ['checked', 'selected', 'data-current-checked', 'aria-disabled'] });
  }
  sync();
}

/** Wire every ring-size picker inside `root` (default: whole document). Idempotent. */
export function r69WireRingSize(root) {
  const scope = root && root.querySelectorAll ? root : document;
  Array.prototype.forEach.call(scope.querySelectorAll('ring-size-select.rss'), wire);
}

if (!window.__r69RingSelect) {
  window.__r69RingSelect = true;
  window.r69WireRingSize = r69WireRingSize;
  const run = () => r69WireRingSize(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  window.addEventListener('load', run);
  // quick-add (and any dynamic injector) fires this after inserting product markup.
  document.addEventListener('r69:rewire', (e) => r69WireRingSize(e.detail && e.detail.root));
}
